import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { APP_ICONS } from 'src/app/icons/icon-provider';
import { JobService } from 'src/app/services/job.service';

/**
 * "Submitted by" / "Posted by" strip beneath the header.
 *
 * Renders name + type with an initials avatar. The right-side action slot
 * is content-projected — parents drop in `<app-connection-template
 * page="jc-v3-candidate-profile" ...>` (or the job variant) to get the
 * Connect / Chat / Cancel / Accept-Reject button set that matches the V3
 * details page (new-candidate / new-job).
 */
@Component({
  selector: 'app-cp-rec-strip',
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './cp-rec-strip.component.html'
})
export class CpRecStripComponent {
  constructor(private jobService: JobService) {}

  @Input() prefix: 'Submitted by' | 'Posted by' = 'Submitted by';
  @Input() name: string = '';
  @Input() role: string = '';
  /** Organisation / business name, if backend supplies it (most responses don't today). */
  @Input() organisation: string = '';
  /** Submitter's profile photo (already URL-normalized by the parent).
   *  When present the avatar shows the image; otherwise it falls back to
   *  initials. `imgFailed` flips back to initials if the image 404s. */
  @Input() photoUrl: string = '';
  /** Optional: explicit name used ONLY for the initials-fallback avatar
   *  (and its hashed background color). When set, initials are derived
   *  from this instead of `name`. Purpose: when the parent overrides
   *  `name` to a label like "You" for the viewer's own record, we still
   *  want the initials to reflect the underlying person ("SA" for
   *  Super Admin, not "Y" for "You"). Non-own callers can leave it blank
   *  — the getter falls back to `name` and preserves the old behavior. */
  @Input() nameForInitials: string = '';
  imgFailed = false;

  /** Source name used for the initials letter AND the avatar-color hash.
   *  Prefers the explicit override so a label like "You" doesn't collapse
   *  to a single letter (or a single-letter hash color) that no longer
   *  identifies the person. Falls back to `name` when the parent hasn't
   *  overridden — preserving the old behaviour for callers that don't
   *  pass `nameForInitials`. */
  private get initialsSource(): string {
    return (this.nameForInitials || this.name || '').trim();
  }

  get initials(): string {
    const v = this.initialsSource;
    if (!v) return '?';
    const parts = v.split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
    return (first + last).toUpperCase() || '?';
  }

  // Avatar colors are delegated to JobService — the SAME methods the
  // applicant card uses (candidate-card-table-view.component.ts:505-509,
  // job.service.ts:328 & 359). This guarantees a person's rec-strip
  // avatar in the slider matches the color of the same person's avatar
  // on the applicant card, exactly. The hash source is `initialsSource`
  // so own items (label="You") still look up the color by the real name.
  get bg(): string {
    return this.jobService.getColor(this.initialsSource);
  }
  get fg(): string {
    return this.jobService.getFullnameTextColor(this.initialsSource);
  }
}
